import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  EMPTY_POOL_CACHE,
  getPoolCache,
  subscribeToPoolCache,
} from '../api/pools';
import type { PoolCache, PoolCacheEntry } from '../types/pool';
import { useAuth } from './AuthContext';

interface PoolCacheContextType {
  // Cache state
  cacheData: PoolCache;
  loading: boolean;
  error: string | null;

  // Helper functions
  refreshCache: () => void;
  searchPools: (searchTerm: string) => PoolCacheEntry[];
  getPoolsForCustomer: (customerId: string) => PoolCacheEntry[];
}

const PoolCacheContext = createContext<PoolCacheContextType | undefined>(
  undefined
);

// Helper to sort pools alphabetically by name
const sortPoolsByName = (pools: PoolCacheEntry[]): PoolCacheEntry[] =>
  [...pools].sort((a, b) => a.poolName.localeCompare(b.poolName));

export const PoolCacheProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { company } = useAuth();
  const [cacheData, setCacheData] = useState<PoolCache>(EMPTY_POOL_CACHE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener (API always sends PoolCache; empty when doc missing or on error)
  useEffect(() => {
    if (!company?.id) {
      setCacheData(EMPTY_POOL_CACHE);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToPoolCache(
      company.id,
      (data) => {
        setCacheData({ ...data, poolsList: sortPoolsByName(data.poolsList) });
        setLoading(false);
        setError(null);
      },
      () => {
        setError('Failed to load pools');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [company?.id]);

  // Search pools using cache (cacheData is always set at API/context level)
  const searchPools = useCallback(
    (searchTerm: string): PoolCacheEntry[] => {
      const searchLower = searchTerm.toLowerCase();
      return cacheData.poolsList.filter(
        (pool) =>
          pool.poolName.toLowerCase().includes(searchLower) ||
          pool.poolAddress.toLowerCase().includes(searchLower) ||
          pool.poolType.toLowerCase().includes(searchLower) ||
          pool.poolStyle.toLowerCase().includes(searchLower) ||
          pool.customerName.toLowerCase().includes(searchLower)
      );
    },
    [cacheData]
  );

  const getPoolsForCustomer = useCallback(
    (customerId: string): PoolCacheEntry[] =>
      cacheData.poolsList.filter((pool) => pool.customerId === customerId),
    [cacheData]
  );

  const refreshCache = useCallback(() => {
    if (!company?.id) return;

    setLoading(true);
    getPoolCache(company.id)
      .then((data) => {
        setCacheData({ ...data, poolsList: sortPoolsByName(data.poolsList) });
        setError(null);
      })
      .catch((err) => {
        console.error('Error refreshing pool cache:', err);
        setError('Failed to refresh pools');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [company?.id]);

  const value: PoolCacheContextType = {
    cacheData,
    loading,
    error,
    searchPools,
    refreshCache,
    getPoolsForCustomer,
  };

  return (
    <PoolCacheContext.Provider value={value}>
      {children}
    </PoolCacheContext.Provider>
  );
};

export const usePoolCacheContext = (): PoolCacheContextType => {
  const context = useContext(PoolCacheContext);
  if (!context) {
    throw new Error(
      'usePoolCacheContext must be used within a PoolCacheProvider'
    );
  }
  return context;
};
